import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function PlansPage() {
  return (
    <DocsLayout
      title="Plans & limits"
      updated="May 9, 2026"
      summary="What every plan includes, the limits we enforce, and how features unlock as you move up."
    >
      <h2>Stage A note (today)</h2>
      <p>
        Rynora is in pre-launch. While we onboard our first users every plan is intentionally generous — features listed
        below as &quot;not available on free&quot; will start enforcing as we approach our first paid customer. We will
        email account owners before any tier limit tightens, and grandfather features for early users where practical.
      </p>

      <h2>What every plan includes</h2>
      <ul>
        <li>Web, API, mobile, and database test flows.</li>
        <li>Run history, traces, screenshots, and per-step logs.</li>
        <li>The Custom Script step (TypeScript, Python, Java).</li>
        <li>Project &amp; environment variables, with secrets encrypted at rest.</li>
        <li>30-day &quot;Recently Deleted&quot; trash window for projects.</li>
        <li>Audit log for every sign-in, change, and admin action.</li>
      </ul>

      <h2>Plan comparison</h2>
      <table>
        <thead>
          <tr>
            <th>Feature</th>
            <th>Free</th>
            <th>Starter</th>
            <th>Pro</th>
            <th>Team</th>
            <th>Enterprise</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Projects</td>
            <td>3</td>
            <td>10</td>
            <td>Unlimited</td>
            <td>Unlimited</td>
            <td>Unlimited</td>
          </tr>
          <tr>
            <td>Runs / month</td>
            <td>100</td>
            <td>500</td>
            <td>2,000</td>
            <td>10,000</td>
            <td>Unlimited</td>
          </tr>
          <tr>
            <td>Parallel workers</td>
            <td>1</td>
            <td>2</td>
            <td>4</td>
            <td>8</td>
            <td>Custom</td>
          </tr>
          <tr>
            <td>AI test generation</td>
            <td>—</td>
            <td>200/mo</td>
            <td>500/mo</td>
            <td>5,000/mo</td>
            <td>Unlimited</td>
          </tr>
          <tr>
            <td>Cross-browser (Firefox, WebKit)</td>
            <td>—</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Self-healing with human approval</td>
            <td>—</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Visual regression (advisory evidence)</td>
            <td>—</td>
            <td>—</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Scheduling</td>
            <td>—</td>
            <td>—</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>CI/CD integration</td>
            <td>—</td>
            <td>—</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>SSO (SAML, OIDC)</td>
            <td>—</td>
            <td>—</td>
            <td>—</td>
            <td>—</td>
            <td>Yes</td>
          </tr>
        </tbody>
      </table>

      <h2>How limits behave</h2>
      <p>
        When you cross a limit Rynora returns an HTTP 402 response with a structured upgrade prompt that the editor
        translates into an in-app banner. We never silently fail or downgrade — you'll always see a message explaining
        which limit was hit and what to do next.
      </p>
      <p>
        Soft limits (like &quot;runs per month&quot;) reset on the first of each calendar month at 00:00 UTC. Hard
        limits (like &quot;projects&quot;) stay in place until you delete a project or move to a higher plan.
      </p>

      <h2>Need a custom plan?</h2>
      <p>
        Hit the <Link to="/contact">contact form</Link> with topic <em>Sales</em> and we'll work out a plan that fits.
        Enterprise customers can negotiate custom worker counts, retention, and SSO.
      </p>
    </DocsLayout>
  );
}
